import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import { Link, useLocation } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./CardSlider.css";
import axios from "axios";

// Shuffle utility with better performance
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Image with fallback and lazy loading
const LazyImage = ({ src, alt, onLoad, onError }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(`data:image/jpeg;base64,${src}`);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    onError?.();
  };

  return (
    <div className="card-image-container" ref={imgRef}>
      {isLoading && (
        <div className="image-placeholder">
          <span className="placeholder-icon">✨</span>
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className="card-image"
          onLoad={handleLoad}
          onError={handleError}
          style={{ opacity: isLoading ? 0 : 1 }}
        />
      )}
    </div>
  );
};

// Premium loader component
const PremiumLoader = () => (
  <div className="premium-loader-wrapper">
    <div className="ring-loader">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
    <p className="loader-text">Discovering matches...</p>
  </div>
);

// Empty state component
const EmptyState = () => (
  <div className="empty-state">
    <div className="empty-icon">💫</div>
    <h3 className="empty-title">No profiles found</h3>
    <p className="empty-text">We're working on finding more matches for you</p>
  </div>
);

// Main Component
const CardSlider = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failedImages, setFailedImages] = useState(new Set());
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const category = query.get("category");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://tulirmatrimony.com/controlapi/customerlisthome.php",
          {
            params: { member: category },
            timeout: 10000,
          }
        );
        
        if (response.data?.body?.length) {
          const shuffledCards = shuffleArray(response.data.body).slice(0, 12);
          setCards(shuffledCards);
        } else {
          setCards([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setCards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [category]);

  const handleImageError = (cardId) => {
    setFailedImages((prev) => new Set(prev).add(cardId));
  };

  const settings = {
    dots: true,
    infinite: cards.length > 4,
    speed: 600,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    pauseOnHover: true,
    cssEase: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    responsive: [
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
        },
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          centerMode: true,
          centerPadding: "20px",
        },
      },
    ],
  };

  if (loading) {
    return <PremiumLoader />;
  }

  if (!cards.length) {
    return <EmptyState />;
  }

  return (
    <>
      <section className="card-slider-section">
        <div className="section-header">
          <span className="section-badge">✨ Premium Matches</span>
          <h2 className="section-title">
            Handpicked <span className="highlight">Profiles</span> For You
          </h2>
          <p className="section-subtitle">
            Discover your perfect match from our curated collection
          </p>
        </div>
        
        <div className="slider-wrapper">
          <Slider {...settings}>
            {cards.map((card, index) => {
              const hasImageFailed = failedImages.has(card.id || index);
              const initials = card.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase() || "👤";

              return (
                <div key={card.id || index} className="slide-item">
                  <div className="premium-card">
                    <div className="card-image-wrapper">
                      {!hasImageFailed && card.image ? (
                        <LazyImage
                          src={card.image}
                          alt={card.name}
                          onError={() => handleImageError(card.id || index)}
                        />
                      ) : (
                        <div className="fallback-avatar">
                          <span className="avatar-initials">{initials}</span>
                          <span className="avatar-icon">💖</span>
                        </div>
                      )}
                      <div className="premium-badge">
                        <span className="badge-icon">⭐</span>
                        <span className="badge-text">Verified</span>
                      </div>
                    </div>
                    
                    <div className="card-content">
                      <div className="profile-info">
                        <h3 className="profile-name">{card.name || "Anonymous"}</h3>
                        <div className="profile-meta">
                          <span className="meta-item">
                            <span className="meta-icon">🎂</span>
                            {card.age || "N/A"} yrs
                          </span>
                          {card.occupation && (
                            <span className="meta-item">
                              <span className="meta-icon">💼</span>
                              {card.occupation}
                            </span>
                          )}
                        </div>
                        {card.location && (
                          <div className="location-tag">
                            <span className="location-icon">📍</span>
                            {card.location}
                          </div>
                        )}
                      </div>
                      
                      <Link to="/Login" className="action-button">
                        <span className="button-text">View Profile</span>
                        <span className="button-icon">→</span>
                      </Link>
                    </div>
                    
                    <div className="card-glow"></div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </section>
    </>
  );
};

export default CardSlider;